import { useState, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight, FiClock, FiUser } from 'react-icons/fi';
import { workSchedulesAPI, usersAPI } from '../../services/api';

const ScheduleCalendar = ({ establishmentId, readOnly = false, onError }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState([]);

  // Date selection
  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1); // 1-12

  // Schedule data map
  const [scheduleData, setScheduleData] = useState({});

  useEffect(() => {
    loadData();
  }, [selectedYear, selectedMonth, establishmentId]);

  const loadData = async () => {
    try {
      setIsLoading(true);

      // Load employees (cleaning staff) for this establishment
      const employeesData = await usersAPI.getAll({
        establishment_id: establishmentId,
        role: 'personal_limpieza'
      });
      setEmployees(employeesData || []);

      // Load schedules for selected month
      const schedulesData = await workSchedulesAPI.getByMonth(
        selectedYear,
        selectedMonth,
        { establishment_id: establishmentId }
      );
      setSchedules(schedulesData || []);

      // Build schedule data structure
      const dataMap = {};
      (schedulesData || []).forEach(schedule => {
        const key = `${schedule.employee_id}_${schedule.date}`;
        dataMap[key] = {
          id: schedule.id,
          hours: schedule.hours,
          shift: schedule.shift || '',
          notes: schedule.notes || ''
        };
      });
      setScheduleData(dataMap);

    } catch (error) {
      console.error('Error loading schedule data:', error);
      if (onError) {
        onError('Error al cargar los datos del cuadrante');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // Get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  // Get weeks in month
  const getWeeksInMonth = (year, month) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = new Date(year, month - 1, 1);
    const firstDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const weeks = [];

    let currentWeek = [];
    let currentDate = 1;

    // Adjust for Monday as first day (Spanish calendar)
    const adjustedFirstDay = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < adjustedFirstDay; i++) {
      currentWeek.push(null);
    }

    // Add all days of the month
    while (currentDate <= daysInMonth) {
      currentWeek.push(currentDate);
      currentDate++;

      // If week is complete (7 days), add it to weeks array
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    // Add remaining days to last week
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }

    return weeks;
  };

  const getScheduleForDate = (employeeId, day) => {
    if (!day) return null;
    const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const key = `${employeeId}_${dateStr}`;
    return scheduleData[key] || null;
  };

  const getShiftLabel = (shift) => {
    switch (shift) {
      case 'morning':
        return 'M';
      case 'afternoon':
        return 'T';
      case 'full':
        return 'C';
      default:
        return '-';
    }
  };

  const getShiftColor = (shift) => {
    switch (shift) {
      case 'morning':
        return 'bg-blue-100 text-blue-800';
      case 'afternoon':
        return 'bg-orange-100 text-orange-800';
      case 'full':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-50 text-gray-400';
    }
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  const weeks = getWeeksInMonth(selectedYear, selectedMonth);

  // Group weeks into pairs (2 weeks per row)
  const weekPairs = [];
  for (let i = 0; i < weeks.length; i += 2) {
    weekPairs.push({
      weeks: weeks.slice(i, i + 2),
      startIndex: i
    });
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (employees.length === 0) {
    return (
      <div className="text-center py-12">
        <FiUser className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No hay personal de limpieza registrado en este establecimiento</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePreviousMonth}
          className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <FiChevronLeft className="h-5 w-5" />
          <span className="hidden sm:inline">Anterior</span>
        </button>

        <h3 className="text-lg font-semibold text-gray-900">
          {monthNames[selectedMonth - 1]} {selectedYear}
        </h3>

        <button
          onClick={handleNextMonth}
          className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <span className="hidden sm:inline">Siguiente</span>
          <FiChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Calendar by week pairs (2 weeks per table) */}
      <div className="space-y-8">
        {weekPairs.map((pair, pairIndex) => (
          <div key={pairIndex} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">
                {pair.weeks.length === 2
                  ? `Semanas ${pair.startIndex + 1} y ${pair.startIndex + 2}`
                  : `Semana ${pair.startIndex + 1}`
                }
              </h4>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50 z-10">
                      Empleado
                    </th>
                    {pair.weeks.map((week, weekIdx) => (
                      week.map((day, dayIndex) => (
                        <th
                          key={`${weekIdx}-${dayIndex}`}
                          className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase min-w-[80px]"
                        >
                          <div>{dayNames[dayIndex]}</div>
                          {day && <div className="text-lg font-semibold text-gray-900 mt-1">{day}</div>}
                        </th>
                      ))
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {employees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white z-10 border-r border-gray-200">
                        <div className="flex items-center space-x-2">
                          <FiUser className="h-4 w-4 text-gray-400" />
                          <span className="truncate max-w-[150px]">{employee.name}</span>
                        </div>
                      </td>
                      {pair.weeks.map((week, weekIdx) => (
                        week.map((day, dayIndex) => {
                          const schedule = getScheduleForDate(employee.id, day);
                          const hasSchedule = schedule && schedule.shift;

                          return (
                            <td
                              key={`${weekIdx}-${dayIndex}`}
                              className="px-3 py-3 text-center min-w-[80px]"
                            >
                              {day ? (
                                <div className="space-y-1">
                                  <div
                                    className={`inline-flex items-center justify-center px-2 py-1 rounded text-xs font-medium ${
                                      hasSchedule ? getShiftColor(schedule.shift) : 'bg-gray-50 text-gray-400'
                                    }`}
                                  >
                                    {hasSchedule ? getShiftLabel(schedule.shift) : '-'}
                                  </div>
                                  {schedule?.hours && (
                                    <div className="flex items-center justify-center text-xs text-gray-500 space-x-1">
                                      <FiClock className="h-3 w-3" />
                                      <span>{schedule.hours}h</span>
                                    </div>
                                  )}
                                  {schedule?.notes && (
                                    <div className="text-xs text-gray-400 truncate max-w-[70px]" title={schedule.notes}>
                                      {schedule.notes}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-gray-300">-</div>
                              )}
                            </td>
                          );
                        })
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Leyenda de Turnos</h4>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center space-x-2">
            <div className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">M</div>
            <span className="text-sm text-gray-600">Mañana</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">T</div>
            <span className="text-sm text-gray-600">Tarde</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">C</div>
            <span className="text-sm text-gray-600">Completo</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleCalendar;
